// src/hooks/useComprasKanban.ts
import { useCallback, useState } from 'react';
import { apiGet } from '../services/api';
import { Compra, OrdemCompra, PreOrdem } from '../types/compras/Compra';
import { usePaginatedFetch } from './usePaginatedFetch';

export const useComprasKanban = () => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [allIdsAbertas, setAllIdsAbertas] = useState<number[]>([]);
  const [allIdsFinalizadas, setAllIdsFinalizadas] = useState<number[]>([]);

  const fetchCompras = useCallback(async (page: number) => {
    if (isSearchActive && searchQuery) {
      const res = await apiGet(`/v1/busca-inteligente/capa-gestao-compras?${searchQuery}&page=${page}&size=10`);
      const section = res.data?.pendentes;
      const items: Compra[] = section?.content || [];
      return { items, totalItems: section?.page?.totalElements || 0 };
    }
    const res = await apiGet(`/v1/ordens-compra/capa-compra-itens?page=${page}&size=10&itens=compra`);
    const items: Compra[] = res.data.content.filter((item: Compra) => item.itensCount['COMPRA'] > 0);
    return { items, totalItems: res.data.page.totalElements };
  }, [isSearchActive, searchQuery]);

  const fetchPreOrdens = useCallback(async (page: number) => {
    if (isSearchActive && searchQuery) {
      const res = await apiGet(`/v1/busca-inteligente/capa-gestao-compras?${searchQuery}&page=${page}&size=10`);
      const section = res.data?.preOrdems;
      const items: PreOrdem[] = section?.content || [];
      return { items, totalItems: section?.page?.totalElements || 0 };
    }
    const res = await apiGet(`/v1/pre-ordens/capa?page=${page}&size=10&status=RASCUNHO&tipoRequisicao=COMPRA`);
    return { items: res.data.content as PreOrdem[], totalItems: res.data.page.totalElements };
  }, [isSearchActive, searchQuery]);

  const fetchOrdensAbertas = useCallback(async (page: number) => {
    if (isSearchActive && searchQuery) {
      const res = await apiGet(`/v1/busca-inteligente/capa-gestao-compras?${searchQuery}&page=${page}&size=10`);
      const section = res.data?.emAberto;
      const items: OrdemCompra[] = section?.content || [];
      if (page === 0) {
        if (Array.isArray(section?.allIds) && section.allIds.length) setAllIdsAbertas(section.allIds);
        else setAllIdsAbertas(items.map((item) => item.id));
      }
      return { items, totalItems: section?.page?.totalElements || 0 };
    }
    const res = await apiGet(`/v1/ordens-compra/capa?page=${page}&size=10`);
    setAllIdsAbertas(res.data.allIds || []);
    return { items: res.data.content as OrdemCompra[], totalItems: res.data.page.totalElements };
  }, [isSearchActive, searchQuery]);

  const fetchOrdensFinalizadas = useCallback(async (page: number) => {
    if (isSearchActive && searchQuery) {
      const res = await apiGet(`/v1/busca-inteligente/capa-gestao-compras?${searchQuery}&page=${page}&size=10`);
      const section = res.data?.finalizadas;
      const items: OrdemCompra[] = section?.content || [];
      if (page === 0) {
        if (Array.isArray(section?.allIds) && section.allIds.length) setAllIdsFinalizadas(section.allIds);
        else setAllIdsFinalizadas(items.map((item) => item.id));
      }
      return { items, totalItems: section?.page?.totalElements || 0 };
    }
    const res = await apiGet(`/v1/ordens-compra/capa?page=${page}&size=10&status=finalizado`);
    setAllIdsFinalizadas(res.data.allIds || []);
    return { items: res.data.content as OrdemCompra[], totalItems: res.data.page.totalElements };
  }, [isSearchActive, searchQuery]);

  const compras = usePaginatedFetch<Compra>({
    fetchFunction: fetchCompras,
    pageSize: 10,
    uniqueKey: 'id',
  });

  const preOrdens = usePaginatedFetch<PreOrdem>({
    fetchFunction: fetchPreOrdens,
    pageSize: 10,
    uniqueKey: 'id',
  });

  const ordensAbertas = usePaginatedFetch<OrdemCompra>({
    fetchFunction: fetchOrdensAbertas,
    pageSize: 10,
    uniqueKey: 'id',
  });

  const ordensFinalizadas = usePaginatedFetch<OrdemCompra>({
    fetchFunction: fetchOrdensFinalizadas,
    pageSize: 10,
    uniqueKey: 'id',
  });

  // Refetch on search change é gerenciado pelo próprio usePaginatedFetch:
  // fetchXxx tem deps [isSearchActive, searchQuery] → loadData muda → reset muda
  // → useEffect interno [reset, enabled] dispara refetch automaticamente.

  const reloadAllColumns = () => {
    compras.reset();
    preOrdens.reset();
    ordensAbertas.reset();
    ordensFinalizadas.reset();
  };

  const handleSearch = (query: string) => {
    setIsSearchActive(true);
    setSearchQuery(query);
  };

  const handleResetSearch = () => {
    setIsSearchActive(false);
    setSearchQuery('');
  };

  return {
    compras,
    preOrdens,
    ordensAbertas,
    ordensFinalizadas,
    isSearchActive,
    allIdsAbertas,
    allIdsFinalizadas,
    handleSearch,
    handleResetSearch,
    reloadAllColumns,
  };
};
